// apps/app/src/components/layout/app-shell.tsx
//
// Two floating panels on a quiet ground: the rail and the content. The top bar
// stays put; `main` is the scroll container beneath it, so navigation swaps the
// page inside it rather than repainting the whole screen.
//
// `main` is positioned on purpose. An ordinary page flows inside the padded,
// width-capped column; a section with its own sub-navigation (Settings) fills
// `main` edge to edge with `absolute inset-0` instead — a full-height
// sub-sidebar beside a column that scrolls on its own.

import { Suspense, useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { PageErrorBoundary } from '@/components/common/page-error-boundary';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAccountLanguage } from '@/lib/profile';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';
import { StateBanner } from '../state-banner';

export function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const scroller = useRef<HTMLElement>(null);
  // The language saved on their account, applied once the profile is known.
  useAccountLanguage();

  // The panel scrolls, not the window, so the router's scroll reset never
  // reaches it: a new page would otherwise open where the last one was left.
  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 });
  }, [pathname]);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-dvh gap-2 overflow-hidden bg-stone-200/60 p-0 text-foreground lg:p-2">
        <Sidebar />

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background lg:rounded-xl lg:border">
          <Topbar />

          {/* Trial, dormant and quota warnings, on the Dashboard only — said
              where the day starts, rather than above every working list. */}
          {pathname === '/' && <StateBanner />}

          <main ref={scroller} id="content" className="scrollbar-none relative min-h-0 flex-1 overflow-y-auto">
            <div className="w-full px-4 py-6 md:px-6">
              <PageErrorBoundary path={pathname}>
                <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
              </PageErrorBoundary>
            </div>
          </main>
        </div>
      </div>

      <Toaster position="bottom-right" />
    </TooltipProvider>
  );
}

/** Matches the real layout's shape and height, so nothing jumps on load. */
function PageSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-28 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );
}
